'use client';
import { LocaleType } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { apartmentValidator, getErrorTranslationKey } from '@/utils/form-rule';
import { Box } from '@mui/material';
import { cva } from 'class-variance-authority';
import { useLocale } from 'next-intl';
import { useTranslations } from '@/i18n/use-translations';
import { useContext, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { AddressFormData } from '.';
import Input, { type FloatingLabelVariant } from '../Form/FormInput';
import useHydratedWhenEnabled from '../Form/useHydratedWhenEnabled';
import { ResponsiveContext } from '../Responsive/ResponsiveProvider';
import WapOnly from '../Responsive/WapOnly';

const FormItemApartment = ({
  formData,
  floatingLabelVariant,
  shrinkLabelWhenFilled = false,
  formItemClassName,
}: {
  formData: UseFormReturn<AddressFormData>;
  floatingLabelVariant?: FloatingLabelVariant;
  shrinkLabelWhenFilled?: boolean;
  formItemClassName?: string;
}) => {
  const t = useTranslations('address');
  const tValidation = useTranslations('components.address.validation');
  const { deviceType } = useContext(ResponsiveContext);
  const [apartment, setApartment] = useState<string>(formData.getValues('apartment'));
  const [isApartmentFocused, setIsApartmentFocused] = useState(false);
  const isHydrated = useHydratedWhenEnabled(shrinkLabelWhenFilled);
  const renderedApartment = shrinkLabelWhenFilled && !isHydrated ? '' : apartment;
  const locale = useLocale() as LocaleType;
  const isWeb = [EPlatform.WEB].includes(deviceType);
  const isInsideFloatingLabel = floatingLabelVariant === 'inside';
  const isApartmentLabelCompact = isInsideFloatingLabel
    ? isApartmentFocused || Boolean(apartment?.trim())
    : !isWeb;
  const apartmentLabel =
    isInsideFloatingLabel && !isWeb && !isApartmentLabelCompact
      ? undefined
      : isApartmentLabelCompact
        ? t('apartment')
        : t('apartmentWebLabel');
  const isMobileInsideEmpty = isInsideFloatingLabel && !isWeb && !isApartmentLabelCompact;
  const apartmentPlaceholderStyle = cva('', {
    variants: {
      locale: {
        fr: 'left-[120px]',
        en: 'left-[106px]',
        de: 'left-[106px]',
        es: 'left-[106px]',
        it: 'left-[106px]',
        pt: 'left-[106px]',
        fi: 'left-[106px]',
        nl: 'left-[106px]',
      },
    },
    defaultVariants: {
      locale: 'en',
    },
  });
  const apartmentPlaceholderPosition = isMobileInsideEmpty
    ? 'left-[12px]'
    : apartmentPlaceholderStyle({ locale });
  formData.watch((value) => {
    setApartment(value.apartment);
  });
  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        '&:focus-within .FormItemApartmentPlaceholder': {
          display: 'none',
        },
      }}
      onFocus={() => setIsApartmentFocused(true)}
      onBlur={() => setIsApartmentFocused(false)}
    >
      <Input
        name="apartment"
        floatingLabelVariant={floatingLabelVariant}
        shrinkLabelWhenFilled={shrinkLabelWhenFilled}
        formItemClassName={formItemClassName}
        label={apartmentLabel}
        slotProps={{
          input: {
            inputProps: {
              maxLength: 20,
            },
          },
        }}
        control={formData.control}
        rules={{
          validate: () => {
            const errorCode = apartmentValidator(formData.getValues('apartment'));

            if (errorCode) {
              const translationKey = getErrorTranslationKey(errorCode);
              return translationKey ? tValidation(translationKey as any) : 'Validation error';
            }
            return true;
          },
        }}
      />
      <WapOnly>
        {!renderedApartment && (
          <div
            className={`FormItemApartmentPlaceholder absolute top-[8px] h-[46px] f14 right-[16px] flex items-center text-auxiliary-dark-gray pointer-events-none ${apartmentPlaceholderPosition}`}
          >
            {t('apartmentWebLabel')}
          </div>
        )}
      </WapOnly>
    </Box>
  );
};

export default FormItemApartment;
